import { FacilitySubscriptionProduct } from './subscription.models';

/** Wording for one confirmation dialog. */
export interface SubscriptionConfirmCopy {
  title: string;
  message: string;
  confirmLabel: string;
}

/** All user-facing copy for a single subscription product. */
export interface SubscriptionProductCopy {
  title: string;
  description: string;
  activate: SubscriptionConfirmCopy;
  deactivate: SubscriptionConfirmCopy;
}

/**
 * Product and dialog copy, kept together so a copy review is a single-file
 * change rather than a hunt through templates.
 *
 * Flex Shifts wording is taken verbatim from the designs — the product was
 * renamed from "Facility Direct Staffing" on 2026-08-12; the API enum is still
 * `TemporaryStaffing`, which is a wire value rather than a label. Job postings
 * dialog wording was not specified and is drafted here — it needs a copy pass.
 */
export const SUBSCRIPTION_PRODUCT_COPY: Record<
  FacilitySubscriptionProduct,
  SubscriptionProductCopy
> = {
  [FacilitySubscriptionProduct.TemporaryStaffing]: {
    title: 'Flex Shifts',
    description:
      'Your subscription enables shift scheduling and billing. You are only ' +
      'charged when shifts are worked. If no shifts are completed in a billing ' +
      'period, no charge applies. Usage is billed based on total shifts worked ' +
      'per month.',
    activate: {
      title: 'Activate Subscription',
      message:
        "You are activating your facility's Flex Shifts " +
        'subscription. As shifts are worked your facility will be responsible ' +
        'to pay contractors that work them.',
      confirmLabel: 'Activate Subscription'
    },
    deactivate: {
      title: 'Deactivate Subscription',
      message:
        "You are deactivating your facility's Flex Shifts " +
        'subscription. You can keep creating shifts until the end of the ' +
        'current billing period, after which it will be disabled. Your last ' +
        'invoice will come at the end of the month.\n\nYou can reactivate ' +
        'any time.',
      confirmLabel: 'Deactivate Subscription'
    }
  },
  [FacilitySubscriptionProduct.JobPostings]: {
    title: 'Job Postings',
    description:
      'This subscription allows facility admins to post permanent job ' +
      'postings and enables contractors to show interest in those postings. ' +
      'The admin can then contact the contractor directly or have the ' +
      'contractor work a shift at their facility to test each other out. ' +
      'better than a simple interview. Post as many job postings as you want. ' +
      'Once activated, it is prorated to the end of the current month and will ' +
      'be billed automatically with your other subscriptions.',
    activate: {
      title: 'Activate Subscription',
      message:
        "You are activating your facility's Job Postings subscription. You can " +
        'post as many job postings as you want. This month is prorated to the ' +
        'end of the current month and billed automatically with your other ' +
        'subscriptions.',
      confirmLabel: 'Activate Subscription'
    },
    deactivate: {
      title: 'Deactivate Subscription',
      message:
        "You are deactivating your facility's Job Postings subscription. You " +
        'can keep posting until the end of the current billing period, after ' +
        'which creating postings will be disabled and your existing postings ' +
        'will stop accepting interest. Your last invoice will come at the end ' +
        'of the month.\n\nYou can reactivate any time.',
      confirmLabel: 'Deactivate Subscription'
    }
  }
};
